import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { service } from './lib/api/service'
import type { Project } from './lib/api/types'
import { NewATAContext, ProjectConfigContext } from './lib/appContext'
import { resolveStatuses, defaultStatusId, STATUS_PIPELINE, type StatusStep, type StatusConfig } from './utils/statusUtils'
import Navigation from './components/Navigation'
import Login from './components/Login'
import Dashboard from './components/Dashboard'
import JournalTable from './components/JournalTable'
import ATADetail from './components/ATADetail'
import UnderrattelserScreen from './components/UnderrattelserScreen'
import SpecifikationerScreen from './components/SpecifikationerScreen'
import ProjectSettings from './components/ProjectSettings'
import NotificationPrint from './components/NotificationPrint'
import ATASpecPrint from './components/ATASpecPrint'
import CombinedPrint from './components/CombinedPrint'
import JournalPrint from './components/JournalPrint'
import APrisLista from './components/APrisLista'
import APrisListaPrint from './components/APrisListaPrint'
import ProjectSettingsPrint from './components/ProjectSettingsPrint'
import NewATADialog from './components/NewATADialog'

// ── Layout ───────────────────────────────────────────────────────────────────

function AppLayout() {
  const [newATAOpen, setNewATAOpen] = useState(false)
  const [project, setProject] = useState<Project | null>(null)
  const [statuses, setStatuses] = useState<StatusStep[]>(STATUS_PIPELINE)
  const [defaultStatus, setDefaultStatus] = useState<string>('upplagd')
  const [hiddenStatuses, setHiddenStatuses] = useState<string[]>([])

  useEffect(() => {
    service.getProject()
      .then(p => {
        setProject(p)
        const cfg: StatusConfig = p?.statusConfig ? JSON.parse(p.statusConfig) : {}
        setStatuses(resolveStatuses(cfg))
        setDefaultStatus(defaultStatusId(cfg))
        setHiddenStatuses(cfg.hidden ?? [])
      })
      .catch(() => {})
  }, [])

  const setStatusConfig = (cfg: { statuses: StatusStep[]; defaultStatus: string; hidden: string[] }) => {
    setStatuses(cfg.statuses.length > 0 ? cfg.statuses : STATUS_PIPELINE)
    setDefaultStatus(cfg.defaultStatus)
    setHiddenStatuses(cfg.hidden)
  }

  const projectLine = project
    ? [project.projektnr, project.projektNamn, project.ort].filter(Boolean).join(' · ')
    : null

  return (
    <ProjectConfigContext.Provider value={{ statuses, defaultStatus, hiddenStatuses, setHiddenStatuses, setStatusConfig }}>
    <NewATAContext.Provider value={{ open: () => setNewATAOpen(true) }}>
      <div className="app">
        <Navigation projectLine={projectLine} />
        <div className="page-container">
          <main className="main-content">
            <Outlet />
          </main>
        </div>
        {newATAOpen && (
          <NewATADialog onClose={() => setNewATAOpen(false)} />
        )}
      </div>
    </NewATAContext.Provider>
    </ProjectConfigContext.Provider>
  )
}

// ── Router ───────────────────────────────────────────────────────────────────

const router = createBrowserRouter([
  // Print routes — rendered without navigation so CSS print works correctly
  { path: 'print/notification/:id', element: <NotificationPrint /> },
  { path: 'print/ata/:id',          element: <ATASpecPrint /> },
  { path: 'print/ata/:id/combined', element: <CombinedPrint /> },
  { path: 'print/journal',          element: <JournalPrint /> },
  { path: 'print/aprislista',       element: <APrisListaPrint /> },
  { path: 'print/projekt',          element: <ProjectSettingsPrint /> },
  {
    element: <AppLayout />,
    children: [
      { index: true,              element: <Dashboard /> },
      { path: 'journal',          element: <JournalTable /> },
      { path: 'journal/:id',      element: <Navigate to="/" replace /> },
      { path: 'underrattelser',   element: <UnderrattelserScreen /> },
      { path: 'specifikationer',  element: <SpecifikationerScreen /> },
      { path: 'ata/:id',          element: <ATADetail /> },
      { path: 'notification/:id', element: <Navigate to="/" replace /> },
      { path: 'projekt',          element: <ProjectSettings /> },
      { path: 'aprislista',       element: <Navigate to="/prislista" replace /> },
      { path: 'prislista',        element: <APrisLista /> },
      { path: '*',                element: <Navigate to="/" replace /> },
    ],
  },
])

// ── Auth wrapper ─────────────────────────────────────────────────────────────

function AuthenticatedApp() {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    service.init().then(() => setReady(true)).catch(err => setError(err.message))
  }, [])

  if (error) return (
    <div className="loading">
      <p style={{ color: 'var(--danger)' }}>Kunde inte ansluta till servern: {error}</p>
    </div>
  )
  if (!ready) return <div className="loading"><div className="spinner" /></div>

  return <RouterProvider router={router} />
}

export default function App() {
  return (
    <>
      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AuthenticatedApp />
      </AuthenticatedTemplate>
    </>
  )
}
